import { getUserSettingsQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/userKeys.js';
import { getUserSettings } from '#pplx/frontend/apps/ask/queries/user-queries/getUserSettings.js';
import { DEFAULT_IS_SIDEBAR_PINNED } from '#pplx/frontend/apps/ask/thread-data/dataTransformation.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { updateCookieUserSettings } from '#pplx/frontend/cookies/metadataCookieManager.js';
import { getCookie } from '#pplx/frontend/cookies/provider.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
const EMPTY_SETTINGS = {
    hasLoadedSettings: false,
    pagesLimit: 0,
    uploadLimit: undefined,
    articleImageUploadLimit: 0,
    defaultImageGenerationModel: 'default',
    defaultVideoGenerationModel: 'default',
    maxFilesPerUser: 0,
    maxFilesPerRepository: 0,
    queryCount: 0,
    queryCountCopilot: 0,
    queryCountMobile: 0,
    hasAiProfile: false,
    disableTraining: false,
    isSidebarCollapsed: false,
    isSidebarPinned: DEFAULT_IS_SIDEBAR_PINNED,
    sidebarHiddenHubs: [],
    subscriptionTier: undefined,
    stripeStatus: 'none',
    revenuecatStatus: 'none',
    revenuecatSource: 'none',
    createLimit: 0,
    notifStatus: '',
    emailStatus: '',
    hasDataRetentionWarning: false,
    allowArticleCreation: false,
    isVerified: false,
    connectors: { connectors: [] },
    connectorLimits: {
        global_file_count: undefined,
        repo_type_limits: undefined,
        max_file_size_mb: undefined,
        max_attachment_file_size_mb: undefined,
        max_attachment_file_size_mb_by_source: undefined,
    },
    alwaysAllowBrowserAgent: false,
    alwaysUseExtendedContext: false,
    inlineVisualizationsEnabled: true,
    viewerAnalyticsEnabled: true,
    computerQueueFollowups: false,
    primaryTeammateContextUuid: null,
    viewerAnalyticsControlAvailable: false,
    viewerAnalyticsDisabledByOrganization: false,
    hasAcceptedApiTerms: false,
    defaultHomepageTab: null,
    effectiveDefaultAskInputMode: 'search',
    organizationDefaultAskInputMode: null,
    sources: {
        source_to_limit: {},
    },
    releasePhase: 'beta',
};
const useIsCollapsedCookie = () => {
    const isCollapsedCookie = getCookie('isCollapsed');
    const { device: { isWindowsApp }, } = useWebPlatform();
    // If no cookie is set, collapse only for Windows app
    if (isCollapsedCookie === null) {
        return isWindowsApp;
    }
    return isCollapsedCookie === 'true';
};
export const useUserSettingsQuery = ({ enabled, reason, refetchOnMount, skipConnectorPickerCredentials = true, }) => {
    const { locale } = useIntl();
    const isUserLoggedIn = useIsUserLoggedIn();
    const isCollapsed = useIsCollapsedCookie();
    const headers = locale ? { 'accept-language': locale } : {};
    const getSettings = () => {
        return getUserSettings({ headers, reason, skipConnectorPickerCredentials });
    };
    const { data, refetch, isLoading, isStale } = useQuery({
        // enabled unless explicitly disabled or user logged out
        enabled: isUserLoggedIn ? (typeof enabled === 'undefined' ? true : enabled) : false,
        queryKey: getUserSettingsQueryKey({ skipConnectorPickerCredentials }),
        queryFn: getSettings,
        refetchOnMount,
        placeholderData: EMPTY_SETTINGS,
        // User settings drive first-paint UI such as Home's default Search/Computer
        // mode. IndexedDB cache can be one setting behind and cause stale-then-fresh
        // visual flashes on reload.
        meta: { skipPersistence: true },
    });
    useEffect(() => {
        if (data) {
            updateCookieUserSettings({
                queryCount: data.queryCount,
                queryCountCopilot: data.queryCountCopilot,
                queryCountMobile: data.queryCountMobile,
            });
        }
    }, [data]);
    const settings = useMemo(() => ({
        ...(data ?? EMPTY_SETTINGS), // placeholder data will exist, but appease typescript
        isSidebarCollapsed: isCollapsed, // override backend value with cookie value
    }), [data, isCollapsed]);
    return useMemo(() => ({
        ...settings,
        refetch,
        isLoading,
        isStale,
    }), [settings, refetch, isLoading, isStale]);
};
//# sourceMappingURL=useUserSettingsQuery.js.map